import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, SHOP_STATUS_EVENT } from "@/lib/api";
import { Preferences } from "@capacitor/preferences";
import { toast } from "sonner";

const AuthContext = createContext(null);

// --- Capacitor Preferences helpers for token persistence ---
const TOKEN_PREF_KEY = "shiftquote_auth_token";

async function saveTokenToPreferences(token) {
  try {
    await Preferences.set({ key: TOKEN_PREF_KEY, value: token });
  } catch (e) {
    console.warn("[Auth] Preferences.set failed (expected on web):", e?.message);
  }
}

async function getTokenFromPreferences() {
  try {
    const { value } = await Preferences.get({ key: TOKEN_PREF_KEY });
    return value || null;
  } catch (e) {
    return null;
  }
}

async function clearTokenFromPreferences() {
  try {
    await Preferences.remove({ key: TOKEN_PREF_KEY });
  } catch (e) {
    // silent
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      let token = localStorage.getItem("auth_token");
      if (!token) {
        token = await getTokenFromPreferences();
        if (token) {
          localStorage.setItem("auth_token", token);
        }
      }
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("auth_token");
        await clearTokenFromPreferences();
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (token, userData) => {
    localStorage.setItem("auth_token", token);
    await saveTokenToPreferences(token);
    setUser(userData);
  }, []);

  const loginWithJWT = useCallback(async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    const { token, user: userData } = res.data;
    localStorage.setItem("auth_token", token);
    await saveTokenToPreferences(token);
    setUser(userData);
    return userData;
  }, []);

  const registerWithJWT = useCallback(async (payload) => {
    const res = await api.post("/auth/register", payload);
    // New behavior: registration no longer returns a token.
    // It returns { message, email, requires_verification }
    // The caller (Login.jsx) handles the redirect to verify-email page.
    return res.data;
  }, []);

  const logout = useCallback(async () => {
    localStorage.removeItem("auth_token");
    await clearTokenFromPreferences();
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch { /* silent */ }
  }, []);

  // shop-status-access-nov2: a 403 shop-status notice from any API call means
  // an admin just suspended / rejected this shop: refresh the account (at most
  // once every 5 s) so the route guard shows the notice screen right away.
  useEffect(() => {
    let last = 0;
    const onStatus = () => {
      const now = Date.now();
      if (now - last < 5000) return;
      last = now;
      refreshUser();
    };
    window.addEventListener(SHOP_STATUS_EVENT, onStatus);
    return () => window.removeEventListener(SHOP_STATUS_EVENT, onStatus);
  }, [refreshUser]);

  // --- Impersonation ---
  const startImpersonation = useCallback((u) => {
    // Save the admin's original token so we can restore it
    const adminToken = localStorage.getItem("auth_token");
    localStorage.setItem("admin_original_token", adminToken);
    // Call the impersonation endpoint to get a token for the target user
    api.post(`/admin/users/${u.user_id}/impersonate`)
      .then((res) => {
        const { token, user: impersonatedUser } = res.data;
        localStorage.setItem("auth_token", token);
        saveTokenToPreferences(token);
        setUser({ ...impersonatedUser, _impersonating: true });
      })
      .catch(() => {
        toast?.error?.("Impersonation failed");
        // Restore original token if the call failed
        localStorage.setItem("auth_token", adminToken);
        localStorage.removeItem("admin_original_token");
      });
  }, []);

  const stopImpersonation = useCallback(async () => {
    const adminToken = localStorage.getItem("admin_original_token");
    if (!adminToken) return;
    localStorage.setItem("auth_token", adminToken);
    localStorage.removeItem("admin_original_token");
    await saveTokenToPreferences(adminToken);
    // Re-fetch the admin user
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch { setUser(null); }
  }, []);

  const startDemoMode = useCallback(async () => {
    const adminToken = localStorage.getItem("auth_token");
    localStorage.setItem("admin_original_token", adminToken);
    try {
      const res = await api.post("/admin/demo/start");
      const { token, shop } = res.data;
      localStorage.setItem("auth_token", token);
      await saveTokenToPreferences(token);
      setUser({ ...shop, _demo: true });
    } catch {
      // Restore on failure
      localStorage.setItem("auth_token", adminToken);
      localStorage.removeItem("admin_original_token");
      toast?.error?.("Demo start failed");
    }
  }, []);

  const stopDemoMode = useCallback(async () => {
    api.post("/admin/demo/end").catch(() => {});
    const adminToken = localStorage.getItem("admin_original_token");
    if (!adminToken) return;
    localStorage.setItem("auth_token", adminToken);
    localStorage.removeItem("admin_original_token");
    await saveTokenToPreferences(adminToken);
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch { setUser(null); }
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, loginWithJWT, registerWithJWT, logout, checkAuth, refreshUser, startImpersonation, stopImpersonation, startDemoMode, stopDemoMode }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
